import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class EmergencyRateLimitInterceptor implements HttpInterceptor {
  private requestCounts: { [url: string]: { count: number; timestamp: number } } = {};
  private readonly MAX_REQUESTS_PER_MINUTE = 10; // Máximo 10 requests por minuto por URL
  private readonly WINDOW_SIZE = 60 * 1000; // 1 minuto en milisegundos
  private readonly BLOCK_DURATION = 5 * 60 * 1000; // 5 minutos de bloqueo

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = request.url;
    const now = Date.now();

    // Limpiar registros antiguos
    this.cleanupOldRecords(now);

    // Verificar si la URL está bloqueada
    if (this.isUrlBlocked(url, now)) {
      console.error(`🚨 EMERGENCIA: URL bloqueada por exceso de llamadas: ${url}`);
      return throwError(() => new Error(`Rate limit exceeded for ${url}. Please wait before making more requests.`));
    }

    // Incrementar contador
    if (!this.requestCounts[url]) {
      this.requestCounts[url] = { count: 0, timestamp: now };
    }

    this.requestCounts[url].count++;

    // Verificar si excede el límite
    if (this.requestCounts[url].count > this.MAX_REQUESTS_PER_MINUTE) {
      console.error(`🚨 EMERGENCIA: Exceso de llamadas detectado para ${url}. Count: ${this.requestCounts[url].count}`);

      // Bloquear la URL por 5 minutos
      this.blockUrl(url, now);

      return throwError(() => new Error(`Rate limit exceeded for ${url}. Please wait before making more requests.`));
    }

    // Log de advertencia si se acerca al límite
    if (this.requestCounts[url].count > this.MAX_REQUESTS_PER_MINUTE * 0.8) {
      console.warn(`⚠️ ADVERTENCIA: Se acerca al límite de llamadas para ${url}. Count: ${this.requestCounts[url].count}/${this.MAX_REQUESTS_PER_MINUTE}`);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si es un error de rate limit, no hacer nada adicional
        if (error.message.includes('Rate limit exceeded')) {
          return throwError(() => error);
        }

        // Para otros errores, verificar si es un patrón de errores repetitivos
        this.checkForErrorPattern(url, error);

        return throwError(() => error);
      })
    );
  }

  private cleanupOldRecords(now: number): void {
    Object.keys(this.requestCounts).forEach(url => {
      const record = this.requestCounts[url];
      if (now - record.timestamp > this.WINDOW_SIZE) {
        delete this.requestCounts[url];
      }
    });
  }

  private isUrlBlocked(url: string, now: number): boolean {
    const blockedKey = `blocked_${url}`;
    const blockedUntil = localStorage.getItem(blockedKey);

    if (blockedUntil) {
      const blockTime = parseInt(blockedUntil);
      if (now < blockTime) {
        return false;
      } else {
        // Remover bloqueo expirado
        localStorage.removeItem(blockedKey);
      }
    }

    return false;
  }

  private blockUrl(url: string, now: number): void {
    const blockedKey = `blocked_${url}`;
    const blockUntil = now + this.BLOCK_DURATION;
    localStorage.setItem(blockedKey, blockUntil.toString());

    console.error(`🚨 EMERGENCIA: URL ${url} bloqueada hasta ${new Date(blockUntil).toLocaleString()}`);
  }

  private checkForErrorPattern(url: string, error: HttpErrorResponse): void {
    const errorKey = `errors_${url}`;
    const errorCount = parseInt(localStorage.getItem(errorKey) || '0');

    if (errorCount > 5) {
      console.error(`🚨 EMERGENCIA: Múltiples errores detectados para ${url}. Count: ${errorCount}`);
      this.blockUrl(url, Date.now());
    } else {
      localStorage.setItem(errorKey, (errorCount + 1).toString());
    }
  }

  // Método para limpiar bloqueos (útil para testing)
  clearBlocks(): void {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('blocked_') || key.startsWith('errors_')) {
        localStorage.removeItem(key);
      }
    });
    this.requestCounts = {};
    console.log('🧹 Emergency Rate Limit: Bloqueos limpiados');
  }

  // Método para obtener estadísticas
  getStats(): { [url: string]: { count: number; isBlocked: boolean } } {
    const stats: { [url: string]: { count: number; isBlocked: boolean } } = {};

    Object.keys(this.requestCounts).forEach(url => {
      stats[url] = {
        count: this.requestCounts[url].count,
        isBlocked: this.isUrlBlocked(url, Date.now())
      };
    });

    return stats;
  }
}
